import { useState, type FormEvent } from 'react';
import { toErrorMessage } from '../api/client';
import type { ClientAccessView } from '../api/types';
import { useToast } from './ToastProvider';

/**
 * The face-match pass line: SFace cosine similarity, shown as 0.000 — the only face-match method the
 * SDKs ship and the server accepts. Bounds mirror Core FaceMatchThresholds, which enforces them.
 */
export const FACE_MATCH_BOUNDS = {
  sface: { floor: 0.2, ceiling: 0.9 },
} as const;

const roundSFace = (v: number) => Math.round(v * 1000) / 1000;

/** The input, shared by Platform Settings (minimum) and Verification Rules (client line). */
export function FaceMatchThresholdInput({
  idPrefix,
  sface,
  onChange,
  sfaceMin,
  disabled,
  help,
}: {
  idPrefix: string;
  sface: number;
  onChange: (sface: number) => void;
  sfaceMin: number;
  disabled?: boolean;
  help?: string;
}) {
  return (
    <div className="row g-3">
      <div className="col-md-6">
        <label htmlFor={`${idPrefix}SFace`} className="form-label small fw-semibold">
          SFace similarity
        </label>
        <div className="input-group">
          <input
            id={`${idPrefix}SFace`}
            className="form-control"
            type="number"
            step="0.001"
            min={sfaceMin}
            max={FACE_MATCH_BOUNDS.sface.ceiling}
            required
            disabled={disabled}
            value={sface}
            onChange={(e) => onChange(Number(e.target.value))}
          />
          <span className="input-group-text">similarity</span>
        </div>
        {help && <div className="form-text">{help}</div>}
      </div>
    </div>
  );
}

/**
 * Settings → Verification Rules (and the client page's Verification Rules tab): the client's own
 * face-match line. It can only be raised: the input stops at the platform minimum and the server
 * refuses anything below it. A line left at the platform minimum is saved as "follow the
 * platform", so it moves when the platform minimum does.
 */
export function FaceMatchThresholdCard({
  access,
  canManage,
  onSave,
  className = 'mb-4',
}: {
  access: ClientAccessView;
  canManage: boolean;
  onSave: (sface: number | null) => Promise<string | null | undefined>;
  className?: string;
}) {
  const { showSuccess, showError } = useToast();
  const [sface, setSFace] = useState(access.faceMatchSFace);
  const [busy, setBusy] = useState(false);

  const platformSFace = access.platformFaceMatchSFace;
  const usingPlatform = access.faceMatchSFaceOverride === null;

  async function save(next: number | null) {
    setBusy(true);
    try {
      showSuccess((await onSave(next)) ?? 'Saved.');
      if (next === null) setSFace(platformSFace);
    } catch (err) {
      showError(toErrorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    const s = roundSFace(sface);
    if (s < platformSFace) {
      showError('The face match threshold cannot be below the platform minimum. It can only be raised.');
      return;
    }
    void save(s === platformSFace ? null : s);
  }

  return (
    <div className={`card ${className}`}>
      <div className="card-header">
        <h6 className="mb-0">
          <i className="bi bi-person-bounding-box me-2 text-primary" />
          Face match threshold
        </h6>
      </div>
      <form onSubmit={handleSubmit}>
        <div className="card-body">
          <p className="small text-body-secondary">
            A selfie that scores below the line against the ID photo is declined. You can make face match
            stricter than the platform minimum, but not looser. Higher lines decline more look-alikes, and
            more genuine customers in poor light.
          </p>
          <FaceMatchThresholdInput
            idPrefix="clientFaceMatch"
            sface={sface}
            onChange={setSFace}
            sfaceMin={platformSFace}
            disabled={!canManage}
            help={`Platform minimum ${platformSFace.toFixed(3)}.`}
          />
          <div className="form-text mt-2">
            {usingPlatform
              ? 'Using the platform minimum.'
              : 'A line at the platform minimum follows it if it changes; a raised platform minimum always applies.'}
          </div>
        </div>
        {canManage && (
          <div className="card-footer bg-transparent d-flex justify-content-end gap-2">
            {!usingPlatform && (
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                disabled={busy}
                onClick={() => void save(null)}
              >
                Use platform minimum
              </button>
            )}
            <button type="submit" className="btn btn-sm btn-primary" disabled={busy}>
              Save face match threshold
            </button>
          </div>
        )}
      </form>
    </div>
  );
}
